import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getRentStatus } from '../../../api/services/propertyService';
import './RentStatus.css';

const RentStatusDetails = ({ propertyId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rentData, setRentData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchRentStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRentStatus(propertyId, selectedYear, selectedMonth);
      setRentData(data.data);
    } catch (err) {
      setError(err.message || 'Failed to fetch rent status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchRentStatus();
  }, [fetchRentStatus]);


  const filteredUnits = useMemo(() => {
    if (!rentData || !rentData.items) return [];
    
    let filtered = rentData.items;
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(unit => {
        if (statusFilter === 'unpaid') return unit.paymentStatus === 'UNPAID';
        if (statusFilter === 'partial') return unit.paymentStatus === 'PARTIAL';
        if (statusFilter === 'paid') return unit.paymentStatus === 'PAID';
        return true;
      });
    }
    
    // Filter by search query (unit name, tenant name, tenant code, unit number)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(unit => {
        return (
          unit.unitName?.toLowerCase().includes(query) ||
          unit.unitNumber?.toLowerCase().includes(query) ||
          unit.tenantName?.toLowerCase().includes(query) ||
          unit.tenantCode?.toLowerCase().includes(query) ||
          unit.tenantEmail?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [rentData, statusFilter, searchQuery]);

  const handleBack = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (onBack) onBack();
  }, [onBack]);

  const handleMonthChange = (e) => {
    const [y, m] = e.target.value.split('-').map(Number);
    setSelectedYear(y);
    setSelectedMonth(m);
  };

  const generateMonthOptions = () => {
    if (!rentData) return [];
    const options = [];
    const { minFilterMonth, minFilterYear, maxFilterMonth, maxFilterYear } = rentData;
    
    let curMonth = minFilterMonth;
    let curYear = minFilterYear;
    
    while (curYear < maxFilterYear || (curYear === maxFilterYear && curMonth <= maxFilterMonth)) {
      const monthLabel = new Date(curYear, curMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
      options.push({ value: `${curYear}-${curMonth}`, label: monthLabel });
      
      curMonth++;
      if (curMonth > 12) {
        curMonth = 1;
        curYear++;
      }
    }
    return options.reverse(); // Show most recent first
  };

  const backButton = onBack ? (
    <button
      type="button"
      className="btn-rent-status-back"
      onClick={handleBack}
      title="Back to properties"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
      <span>Back to properties</span>
    </button>
  ) : null;

  if (loading && !rentData) {
    return (
      <div className="rent-status-page">
        {backButton && (
          <div className="rent-status-back-row">{backButton}</div>
        )}
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Fetching rent data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rent-status-container">
        <div className="error-card">
          <span className="error-icon">⚠️</span>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button className="btn-retry" onClick={fetchRentStatus}>Try Again</button>
          <button className="btn-back-simple" onClick={handleBack}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rent-status-page">
      <header className="page-header-premium">
        <div className="header-left header-left--stacked">
          {backButton}
          <div className="title-group">
            <h1 className="page-title-main">{rentData?.propertyName || 'Rent Status'}</h1>
            <p className="page-subtitle">
              Period: {new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="header-actions-premium">
          <div className="search-box-pill">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Filter units or tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="filter-group-premium">
            <select 
              className="select-pill"
              value={`${selectedYear}-${selectedMonth}`}
              onChange={handleMonthChange}
            >
              {generateMonthOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select 
              className="select-pill"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Status: All</option>
              <option value="paid">Status: Paid</option>
              <option value="partial">Status: Partial</option>
              <option value="unpaid">Status: Unpaid</option>
            </select>
          </div>
        </div>
      </header>

      <main className="content-main">
        {filteredUnits.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon">📭</div>
            <h3>No records found</h3>
            <p>We couldn't find any rent records for this selection.</p>
          </div>
        ) : (
          <div className="table-responsive-wrapper">
            <table className="premium-table desktop-only">
              <thead>
                <tr>
                  <th>UNIT</th>
                  <th>TENANT</th>
                  <th>RENT</th>
                  <th>WATER</th>
                  <th>PENALTY</th>
                  <th>TOTAL DUE</th>
                  <th>PAID</th>
                  <th>REMAINING</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnits.map((item) => (
                  <tr key={item.unitId} className="table-row-hover">
                    <td>
                      <div className="unit-tag">{item.unitNumber}</div>
                    </td>
                    <td>
                      <div className="tenant-meta">
                        <span className="tenant-name-bold">{item.tenantName}</span>
                        <span className="tenant-subtext">{item.tenantCode}</span>
                      </div>
                    </td>
                    <td>KES {item.rentAmount?.toLocaleString()}</td>
                    <td>KES {item.waterAmount?.toLocaleString()}</td>
                    <td>KES {item.penaltyAmount?.toLocaleString()}</td>
                    <td className="amount-bold">KES {item.totalDue?.toLocaleString()}</td>
                    <td className="amount-success">KES {item.totalPaid?.toLocaleString()}</td>
                    <td className={item.remainingDue > 0 ? 'amount-danger' : 'amount-success'}>
                      KES {item.remainingDue?.toLocaleString()}
                    </td>
                    <td>
                      <span className={`pill-badge badge-${item.paymentStatus?.toLowerCase()}`}>
                        {item.paymentStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mobile-only units-list-mobile">
              {filteredUnits.map((item) => (
                <div key={item.unitId} className="unit-card-mobile-premium">
                  <div className="card-mobile-header">
                    <div className="unit-id-pill">{item.unitNumber}</div>
                    <span className={`pill-badge badge-${item.paymentStatus?.toLowerCase()}`}>
                      {item.paymentStatus}
                    </span>
                  </div>
                  
                  <div className="card-mobile-body">
                    <h3 className="mobile-tenant-name">{item.tenantName}</h3>
                    <p className="mobile-tenant-meta">{item.tenantCode}</p>
                    
                    <div className="mobile-stats-row">
                      <div className="m-stat">
                        <label>Due</label>
                        <span className="val">KES {(item.totalDue || 0).toLocaleString()}</span>
                      </div>
                      <div className="m-stat">
                        <label>Paid</label>
                        <span className="val success">KES {(item.totalPaid || 0).toLocaleString()}</span>
                      </div>
                      <div className="m-stat">
                        <label>Remaining</label>
                        <span className={`val ${item.remainingDue > 0 ? 'danger' : 'success'}`}>
                          KES {(item.remainingDue || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RentStatusDetails;
